import escapeRegExp from 'lodash/escapeRegExp';

/**
 * What a turn's error card shows when a subscription failed it: the firewall's refusals, for any
 * provider, and the provider's own failures, which its descriptor's `errors` recognizes. Both are
 * read by the code the error carries. Client-safe: no server imports.
 */
import type { AiSubscriptionErrorAction, AiSubscriptionProvider } from './providers';

import {
  AI_SUBSCRIPTION_FIREWALL_CODE_PREFIX,
  aiSubscriptionFirewallCards,
  aiSubscriptionFirewallRefusals,
  parseAiSubscriptionFirewallCode,
} from './policy';
import {
  AI_SUBSCRIPTION_DESCRIPTORS,
  AI_SUBSCRIPTION_PROVIDERS,
  isAiSubscriptionProvider,
} from './providers';

import { getNonEmptyString, getRecord } from '@/lib/utils/unknown';

/**
 * What a turn's failure on a subscription offers beside its message, which the transcript keeps
 * on the card: the provider's connect dialog or usage page, and whether Retry can work.
 */
export type AiSubscriptionErrorOffer =
  | { provider: AiSubscriptionProvider; action: AiSubscriptionErrorAction; retry: boolean }
  /** A provider this bundle doesn't know, from a newer deployment: nothing to offer but Retry. */
  | { provider: null; action: null; retry: boolean };

/** A turn that failed on a subscription, as its error card shows it. */
export type AiSubscriptionErrorView = AiSubscriptionErrorOffer & { message: string };

/**
 * The firewall's refusals for a provider this bundle doesn't know, in neutral words: the same
 * reasons and Retry, with the provider's connect dialog out of reach.
 */
const NEUTRAL_FIREWALL_CARDS = aiSubscriptionFirewallCards({
  plan: 'AI subscription',
  vendor: 'AI subscription',
});

/**
 * Where a subscription's failure codes start: the firewall's, for any provider, and each known
 * provider's own. A word elsewhere in an error is never read as a code.
 */
const ERROR_CODE = new RegExp(
  `\\b(?:${[
    AI_SUBSCRIPTION_FIREWALL_CODE_PREFIX,
    ...AI_SUBSCRIPTION_PROVIDERS.flatMap(
      (provider) => AI_SUBSCRIPTION_DESCRIPTORS[provider].errors.codePrefixes,
    ),
  ]
    .map(escapeRegExp)
    .join('|')})[a-z0-9_]+\\b`,
  'g',
);

/**
 * Each provider's `sessionCodes` as whole words, which only the errors of a session on that
 * provider are searched for.
 */
const SESSION_CODES = new Map(
  AI_SUBSCRIPTION_PROVIDERS.flatMap((provider) => {
    const codes = AI_SUBSCRIPTION_DESCRIPTORS[provider].errors.sessionCodes;
    return codes.length === 0
      ? []
      : [[provider, new RegExp(`\\b(?:${codes.map(escapeRegExp).join('|')})\\b`, 'g')] as const];
  }),
);

/**
 * The firewall's refusal `code` names, in its provider's words, or in neutral ones for a provider
 * this bundle doesn't know.
 */
function firewallErrorView(code: string): AiSubscriptionErrorView | null {
  const named = parseAiSubscriptionFirewallCode(code);
  if (named === null) {
    return null;
  }
  if (isAiSubscriptionProvider(named.provider)) {
    const { message, action, retry } = aiSubscriptionFirewallRefusals(named.provider)[named.reason];
    return { provider: named.provider, message, action, retry };
  }
  return { ...NEUTRAL_FIREWALL_CARDS[named.reason], provider: null, action: null };
}

/** What the provider whose API names `code` says it means, read beside it in `body`. */
function providerErrorView(code: string, body: string): AiSubscriptionErrorView | null {
  for (const provider of AI_SUBSCRIPTION_PROVIDERS) {
    const { errors } = AI_SUBSCRIPTION_DESCRIPTORS[provider];
    if (errors.codePrefixes.some((prefix) => code.startsWith(prefix))) {
      const card = errors.card(code, body, aiSubscriptionFirewallRefusals(provider));
      return card === null ? null : { provider, ...card };
    }
  }
  return null;
}

/** What the session's own provider says a code it shares with other APIs means (`sessionCodes`). */
function sessionErrorView(
  provider: AiSubscriptionProvider,
  texts: readonly string[],
): AiSubscriptionErrorView | null {
  const codes = SESSION_CODES.get(provider);
  if (codes === undefined) {
    return null;
  }
  const { errors } = AI_SUBSCRIPTION_DESCRIPTORS[provider];
  for (const text of texts) {
    for (const [code] of text.matchAll(codes)) {
      const card = errors.card(code, text, aiSubscriptionFirewallRefusals(provider));
      if (card !== null) {
        return { provider, ...card };
      }
    }
  }
  return null;
}

/**
 * A turn's error as its card shows it when a subscription failed it, or null for any other error.
 * Reads the code in the error's raw response body, which OpenCode keeps (`response.body`), then
 * in its message, never its type: OpenCode files a subscription's failures under generic types
 * such as `provider.rate-limit` or `provider.auth`, which say nothing of the subscription. The
 * first known code decides. In a session that runs on a subscription, `sessionProvider`, that
 * provider's shared codes count too, after any code only a subscription uses.
 */
export function aiSubscriptionErrorView(
  error: unknown,
  sessionProvider?: AiSubscriptionProvider,
): AiSubscriptionErrorView | null {
  const texts = errorTexts(error);
  for (const text of texts) {
    for (const [code] of text.matchAll(ERROR_CODE)) {
      const view = firewallErrorView(code) ?? providerErrorView(code, text);
      if (view !== null) {
        return view;
      }
    }
  }
  return sessionProvider === undefined ? null : sessionErrorView(sessionProvider, texts);
}

/**
 * What a subscription's error card says to anyone but the subscription's owner, whose words
 * (connect, reconnect, the usage limit) are for the owner alone: whose subscription stopped the
 * turn, and that only they can fix it. `provider` is null for one this bundle doesn't know.
 */
export function aiSubscriptionErrorForTeammate(
  provider: AiSubscriptionProvider | null,
  ownerName: string | null,
): string {
  const subscription =
    provider === null ? 'AI subscription' : AI_SUBSCRIPTION_DESCRIPTORS[provider].names.plan;
  return ownerName === null
    ? `This turn stopped on the session creator’s ${subscription}. Only they can fix that and continue the session.`
    : `This turn stopped on ${ownerName}’s ${subscription}. Only ${ownerName} can fix that and continue the session.`;
}

/** An error's raw response body, then its message, where either is text. */
function errorTexts(error: unknown): string[] {
  if (typeof error === 'string') {
    return [error];
  }
  const record = getRecord(error);
  return [getRecord(record?.response)?.body, record?.message]
    .map(getNonEmptyString)
    .filter((text) => text !== null);
}
